import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, CircleNotch, Eye, EyeSlash } from '@phosphor-icons/react';

function formatApiErrorDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Cloudflare "origin unreachable / bad response" family — 520-525 plus 502/504.
// When we see one of these, the container is almost certainly cold-starting
// (or Cloudflare timed out the initial connection) and a retry after a short
// delay usually succeeds. We also detect HTML bodies (Cloudflare error pages)
// where the axios error carries the raw HTML text as the response body.
function isColdStartSignature(err) {
  if (!err) return false;
  if (err.code === 'ECONNABORTED') return true; // axios timeout
  const status = err.response?.status;
  if (status === 502 || status === 504) return true;
  if (status && status >= 520 && status <= 525) return true;
  const body = err.response?.data;
  if (typeof body === 'string' && /<!DOCTYPE html|cloudflare|origin web server/i.test(body)) return true;
  return false;
}

function describeLoginError(err) {
  // Diagnose by HTTP status / network condition
  if (err.response) {
    const { status, data } = err.response;
    const detailMsg = formatApiErrorDetail(data?.detail);
    if (status === 401) return detailMsg || "Wrong email or password.";
    if (status === 429) return "Too many attempts. Please wait and try again.";
    if (status === 500) return "Server error (500). Please retry or contact admin.";
    if (status === 502 || status === 503 || status === 504) return "Server is warming up. Please retry in a few seconds.";
    if (status >= 520 && status <= 525) return "Server is waking from idle. Please retry in a few seconds.";
    return detailMsg || `Request failed (HTTP ${status}).`;
  }
  // No response object → network / CORS / DNS / mixed-content
  if (err.code === 'ERR_NETWORK') return "Network error — backend unreachable. Check internet or CORS config.";
  if (err.message?.includes('Network Error')) return "Network error — backend unreachable. Check internet or CORS config.";
  if (err.message?.includes('timeout')) return "Timeout — server may be waking up. Please retry.";
  return err.message || "Unknown error. Open DevTools → Network → click login request for details.";
}

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');    // transient status banner (e.g. "Server waking, retrying…")
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setStatus('');
    setLoading(true);

    // Try up to 3 times when we detect a Cloudflare cold-start signature.
    // Backoff: 2s → 6s → 12s. The origin container needs ~20-30s to fully
    // wake so 3 attempts covers virtually every real-world cold start.
    const backoffMs = [2000, 6000, 12000];
    let lastErr = null;
    for (let attempt = 0; attempt < backoffMs.length; attempt++) {
      try {
        await login(email, password);
        navigate('/');
        return;
      } catch (err) {
        lastErr = err;
        // Wrong password / real 4xx errors: no point retrying.
        const s = err?.response?.status;
        if (s === 401 || s === 403 || s === 429 || s === 422) break;
        if (!isColdStartSignature(err) && attempt === 0) break;
        // Cold-start signature — retry after backoff.
        if (attempt < backoffMs.length - 1) {
          const waitMs = backoffMs[attempt];
          setStatus(`Server is waking up… retrying in ${waitMs / 1000}s (attempt ${attempt + 2}/${backoffMs.length}).`);
          await sleep(waitMs);
        }
      }
    }
    setStatus('');
    if (lastErr) setError(describeLoginError(lastErr));
    setLoading(false);
  };

  return (
    <div className="min-h-screen grid-pattern flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white border border-gray-200 rounded p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#002FA7] rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock size={32} weight="bold" className="text-white" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900" style={{ fontFamily: 'var(--font-heading)' }}>
              Admin Login
            </h1>
            <p className="text-sm text-gray-500 mt-2">
              Sign in to manage draw data
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {status && !error && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded text-sm flex items-center gap-2" data-testid="login-status">
                <CircleNotch size={16} className="animate-spin flex-shrink-0" />
                <span>{status}</span>
              </div>
            )}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-wider font-medium text-gray-500">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                required
                className="form-input"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider font-medium text-gray-500">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="form-input pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  title={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800 focus:outline-none"
                  data-testid="login-password-toggle"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeSlash size={18} weight="regular" /> : <Eye size={18} weight="regular" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-primary flex items-center justify-center gap-2"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <CircleNotch size={18} className="animate-spin" />
                  {status ? 'Waking server…' : 'Signing in...'}
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
        </div>

        <p className="text-center text-xs text-gray-400 mt-4">
          Ashish Analytical Application
        </p>
      </div>
    </div>
  );
}
